import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { createLead } from '../../lib/api';
import { toast } from 'sonner';

const BUYER_TYPES = ['Retailer', 'Wholesaler', 'Reseller', 'Corporate', 'Export'];

export const LeadFormDialog = ({
  isOpen,
  onClose,
  sourcePageUrl,
  interestCategoryId,
  interestBrandId,
  interestProductId,
  showBuyerType = true,
  title = 'Request a Quote',
  subtitle = 'Fill in your details and we\'ll get back to you shortly',
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    buyer_type: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createLead({
        ...formData,
        buyer_type: formData.buyer_type || null,
        source_page_url: sourcePageUrl || window.location.href,
        interest_category_id: interestCategoryId || null,
        interest_brand_id: interestBrandId || null,
        interest_product_id: interestProductId || null,
      });

      // Track conversion event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'lead_form_submit', {
          source_page: sourcePageUrl,
        });
      }

      toast.success('Thank you! We will contact you soon.');
      onClose();
      navigate('/thank-you');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Lead submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white p-0 overflow-hidden" data-testid="lead-form-dialog">
        {/* Header */}
        <div className="charcoal-gradient px-6 py-8 text-white">
          <DialogHeader>
            <DialogTitle className="heading-md text-white mb-2">{title}</DialogTitle>
            <p className="text-gray-300 text-sm">{subtitle}</p>
          </DialogHeader>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="label-text">Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input-luxury"
                placeholder="Your name"
                data-testid="lead-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company" className="label-text">Company</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="input-luxury"
                placeholder="Company name"
                data-testid="lead-form-company"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-text">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="input-luxury"
                placeholder="your@email.com"
                data-testid="lead-form-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="label-text">Phone *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                className="input-luxury"
                placeholder="+91 9998863360"
                data-testid="lead-form-phone"
              />
            </div>
          </div>

          {showBuyerType && (
            <div className="space-y-2">
              <Label className="label-text">Buyer Type</Label>
              <Select 
                value={formData.buyer_type} 
                onValueChange={(val) => setFormData({ ...formData, buyer_type: val })}
              >
                <SelectTrigger className="input-luxury">
                  <SelectValue placeholder="Select your business type" />
                </SelectTrigger>
                <SelectContent>
                  {BUYER_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="message" className="label-text">Message</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="input-luxury min-h-[100px] resize-none"
              placeholder="Tell us about your requirements..."
              data-testid="lead-form-message"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 btn-secondary"
              data-testid="lead-form-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary"
              data-testid="lead-form-submit"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormDialog;
